// src/components/ProjectForm.jsx
import React, { useState, useEffect, useRef } from "react";
import { FaCamera, FaCheck, FaTimes, FaEllipsisH } from "react-icons/fa";
import { supabase } from "../supabaseClient";
import "./ProjectForm.css";

const TIPOS = [
  { key: "projeto", label: "Projeto" },
  { key: "setor", label: "Setor" },
  { key: "workspace", label: "Workspace" },
];

export default function ProjectForm({
  isOpen,
  onClose,
  onSave,
  initialData = null,
  containerAtual,
  profile,
  isEditing = false,
}) {
  const engenheiroInputRef = useRef(null);

  const [formData, setFormData] = useState({
    tipo: "projeto",
    name: "",
    engenheiroResponsavel: null,
    engenheiroTexto: "",
    photoFile: null,
    photoUrl: null,
  });

  const [sugestoesEngenheiro, setSugestoesEngenheiro] = useState([]);
  const [mostrarSugestoesEngenheiro, setMostrarSugestoesEngenheiro] = useState(false);

  const buscarMembrosDoContainer = async (termo) => {
    if (!termo.trim() || !containerAtual) return [];

    try {
      const { data: convites } = await supabase
        .from("convites")
        .select("user_id")
        .eq("remetente_id", containerAtual)
        .eq("status", "aceito");

      const userIds = convites?.map((c) => c.user_id).filter(Boolean) || [];
      if (userIds.length === 0) return [];

      const { data: perfis } = await supabase
        .from("profiles")
        .select("id, nickname, avatar_url")
        .in("id", userIds)
        .ilike("nickname", `%${termo}%`)
        .limit(5);

      return perfis || [];
    } catch (err) {
      console.error("Erro ao buscar membros do container:", err);
      return [];
    }
  };

  useEffect(() => {
    if (isOpen && initialData) {
      setFormData({
        tipo: initialData.tipo || "projeto",
        name: initialData.name || "",
        engenheiroResponsavel: initialData.engenheiroResponsavel || null,
        engenheiroTexto: initialData.engenheiroResponsavel
          ? `@${initialData.engenheiroResponsavel.nickname}`
          : "",
        photoFile: null,
        photoUrl: initialData.photoUrl || null,
      });
    } else if (isOpen && !isEditing) {
      setFormData({
        tipo: "projeto",
        name: "",
        engenheiroResponsavel: null,
        engenheiroTexto: "",
        photoFile: null,
        photoUrl: null,
      });
    }
  }, [isOpen, initialData, isEditing]);

  const buscarSugestoesEngenheiro = async (termo) => {
    const resultados = await buscarMembrosDoContainer(termo);
    setSugestoesEngenheiro(resultados);
  };

  const handleEngenheiroChange = (e) => {
    const valor = e.target.value;
    setFormData((prev) => ({
      ...prev,
      engenheiroTexto: valor,
      engenheiroResponsavel: null,
    }));

    const pos = e.target.selectionStart;
    const antes = valor.substring(0, pos);
    const ultimaArroba = antes.lastIndexOf("@");
    if (ultimaArroba !== -1) {
      const termo = antes.substring(ultimaArroba + 1).trim();
      if (termo) {
        setMostrarSugestoesEngenheiro(true);
        buscarSugestoesEngenheiro(termo);
      } else {
        setMostrarSugestoesEngenheiro(false);
      }
    } else {
      setMostrarSugestoesEngenheiro(false);
    }
  };

  const inserirEngenheiro = (perfil) => {
    setFormData((prev) => ({
      ...prev,
      engenheiroResponsavel: perfil,
      engenheiroTexto: `@${perfil.nickname}`,
    }));
    setMostrarSugestoesEngenheiro(false);
  };

  const removerEngenheiro = () => {
    setFormData((prev) => ({
      ...prev,
      engenheiroResponsavel: null,
      engenheiroTexto: "",
    }));
    setTimeout(() => engenheiroInputRef.current?.focus(), 0);
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setFormData((prev) => ({
        ...prev,
        photoFile: file,
        photoUrl: URL.createObjectURL(file),
      }));
    }
  };

  const handleSubmit = () => {
    if (!formData.name.trim()) return;
    onSave({
      ...(initialData || {}),
      tipo: formData.tipo,
      name: formData.name,
      engenheiroResponsavel: formData.engenheiroResponsavel,
      photoFile: formData.photoFile,
      photoUrl: formData.photoUrl,
    });
  };

  if (!isOpen) return null;

  const tipoAtual = TIPOS.find((t) => t.key === formData.tipo) || TIPOS[0];

  return (
    <div className="project-modal-overlay" onClick={onClose}>
      <div className="project-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="project-modal-header">
          <div>
            <h2>{isEditing ? "Editar" : "Novo"} {tipoAtual.label}</h2>
            <p className="project-modal-subtitle">
              Defina o essencial agora. O resto você organiza depois de criar.
            </p>
          </div>
          <button type="button" className="modal-close-btn" onClick={onClose} aria-label="Fechar">
            <FaTimes />
          </button>
        </div>

        {!isEditing && (
          <div className="type-tabs">
            {TIPOS.map((t) => (
              <button
                key={t.key}
                type="button"
                className={`type-tab ${formData.tipo === t.key ? "type-tab--active" : ""}`}
                onClick={() => setFormData((prev) => ({ ...prev, tipo: t.key }))}
              >
                {t.label}
              </button>
            ))}
          </div>
        )}

        <div className="project-modal-body">
          <div className="project-modal-left">
            <span className="preview-label">Pré-visualização</span>

            {formData.tipo === "setor" && (
              <label htmlFor="photo-upload" className="preview-card preview-card--setor">
                <div className="preview-setor-photo">
                  {formData.photoUrl ? (
                    <img src={formData.photoUrl} alt="Foto" />
                  ) : (
                    <FaCamera />
                  )}
                </div>
                <span className="preview-setor-name">{formData.name || "Nome do Setor"}</span>
                <FaEllipsisH className="preview-setor-menu" />
              </label>
            )}

            {formData.tipo === "workspace" && (
              <label htmlFor="photo-upload" className="preview-card preview-card--workspace">
                <div className="preview-workspace-photo">
                  {formData.photoUrl ? (
                    <img src={formData.photoUrl} alt="Foto" />
                  ) : (
                    <FaCamera />
                  )}
                </div>
                <div className="preview-workspace-name">
                  {formData.name || "Workspace"}
                </div>
              </label>
            )}

            {formData.tipo === "projeto" && (
              <label htmlFor="photo-upload" className="project-preview-card">
                <div className="preview-image-wrapper">
                  {formData.photoUrl ? (
                    <img src={formData.photoUrl} alt="Foto" className="preview-image" />
                  ) : (
                    <div className="preview-image-placeholder">
                      <FaCamera />
                      <span>Adicionar foto</span>
                    </div>
                  )}
                  <span className="preview-badge">{tipoAtual.label}</span>
                  <span className="preview-image-overlay">
                    <FaCamera />
                    Alterar foto
                  </span>
                </div>
                <div className="preview-title">
                  {formData.name || `Nome do ${tipoAtual.label}`}
                </div>
              </label>
            )}

            <input
              type="file"
              id="photo-upload"
              accept="image/*"
              onChange={handlePhotoChange}
              hidden
            />
          </div>

          <div className="project-modal-right">
            <div className="form-group">
              <label>Nome do {tipoAtual.label}</label>
              <input
                type="text"
                className="input-lg"
                value={formData.name}
                onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
                placeholder="Ex: Hortus Park"
                autoFocus
              />
            </div>

            <div className="form-group">
              <label>Engenheiro Responsável</label>
              <div className="engineer-row">
                <div className="engineer-input-wrapper">
                  <input
                    ref={engenheiroInputRef}
                    type="text"
                    className="input-lg"
                    value={formData.engenheiroTexto}
                    onChange={handleEngenheiroChange}
                    placeholder="Digite @ para mencionar"
                    autoComplete="off"
                    onBlur={() => setTimeout(() => setMostrarSugestoesEngenheiro(false), 200)}
                    onKeyPress={(e) => {
                      if (e.key === "Enter" && mostrarSugestoesEngenheiro && sugestoesEngenheiro.length > 0) {
                        e.preventDefault();
                        inserirEngenheiro(sugestoesEngenheiro[0]);
                      }
                    }}
                  />
                  {mostrarSugestoesEngenheiro && sugestoesEngenheiro.length > 0 && (
                    <div className="suggestions-dropdown">
                      {sugestoesEngenheiro.map((sug) => (
                        <div
                          key={sug.id}
                          onClick={() => inserirEngenheiro(sug)}
                          onMouseDown={(e) => e.preventDefault()}
                          className="suggestion-item"
                        >
                          {sug.avatar_url ? (
                            <img src={sug.avatar_url} alt="" />
                          ) : (
                            <div className="suggestion-avatar-placeholder">
                              {sug.nickname?.charAt(0).toUpperCase() || "?"}
                            </div>
                          )}
                          {sug.nickname}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {formData.engenheiroResponsavel && (
                  <div className="engineer-avatar-container">
                    {formData.engenheiroResponsavel.avatar_url ? (
                      <img
                        src={formData.engenheiroResponsavel.avatar_url}
                        alt={formData.engenheiroResponsavel.nickname}
                      />
                    ) : (
                      <div className="engineer-avatar-placeholder">
                        {formData.engenheiroResponsavel.nickname?.charAt(0).toUpperCase()}
                      </div>
                    )}
                    <button
                      type="button"
                      className="remove-engineer-btn"
                      onClick={removerEngenheiro}
                    >
                      ×
                    </button>
                  </div>
                )}
              </div>
              <span className="field-hint">Opcional — pode ser definido depois</span>
            </div>
          </div>
        </div>

        <div className="modal-actions">
          <button className="projform-cancel-btn" onClick={onClose}>
            Cancelar
          </button>
          <button
            className="projform-save-btn"
            onClick={handleSubmit}
            disabled={!formData.name.trim()}
          >
            <FaCheck />
            {isEditing ? "Salvar alterações" : `Criar ${tipoAtual.label}`}
          </button>
        </div>
      </div>
    </div>
  );
}